import { useCallback, useEffect, useState } from 'react'
import { Messenger } from '@firework/web'
import '@firework/web/web.css'
import apiClient from '@/api/client'
import Spinner from '@/components/common/Spinner'

/**
 * The parish message center: the embedded Firework messenger, unbranded and in CatCloud's colours (see .parish-messenger in styles/index.css).
 * CatCloud's backend mints the session (POST /api/v1/messenger/session), so users never see a second
 * login, and each parish is its own messaging space.
 */
interface Session {
  server_url: string
  token: string
  expires_at?: string | null
  deployment_id: string
  encryption_mode: 'server' | 'e2e'
}

const MAX_ATTACHMENT_BYTES = 1024 * 1024 // spec: attachments under 1 MB

// Spec section 7 reactions (OpenMoji codepoints), served from public/openmoji (no CDN).
const REACTIONS = [
  { code: '2764', label: 'heart' }, { code: '1F4AF', label: '100%' }, { code: '2705', label: 'check' },
  { code: '1F64F', label: 'prayer' }, { code: '1F602', label: 'laughing' }, { code: '1F622', label: 'crying' },
  { code: '1F44D', label: 'thumbs up' }, { code: '1F44E', label: 'thumbs down' }, { code: '1F525', label: 'fire' },
  { code: '1F440', label: 'eyes' }, { code: '270D', label: 'writing' }, { code: '1F44F', label: 'clapping' },
  { code: '1F631', label: 'scream' }, { code: '1F3C6', label: 'trophy' }, { code: '1FAE1', label: 'salute' },
  { code: '26A1', label: 'lightning' }, { code: '1F615', label: 'confused' },
]

export default function ParishMessenger() {
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const { data } = await apiClient.post<Session>('/api/v1/messenger/session')
      setSession(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Messages are unavailable right now.')
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Host tokens are short-lived: fetch a fresh one shortly before it expires.
  useEffect(() => {
    if (!session?.expires_at) return
    const ms = new Date(session.expires_at).getTime() - Date.now() - 5 * 60 * 1000
    const t = setTimeout(load, Math.max(ms, 60 * 1000))
    return () => clearTimeout(t)
  }, [session?.expires_at, load])

  if (error) {
    return (
      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 flex items-center justify-between gap-3">
        <span>{error}</span>
        <button className="btn-secondary text-xs" onClick={load}>Try again</button>
      </div>
    )
  }
  if (!session) return <Spinner label="Loading messages…" />

  return (
    <div className="parish-messenger rounded-xl border border-gray-200 overflow-hidden bg-white" style={{ height: 600 }}>
      <Messenger
        key={session.deployment_id}
        serverUrl={session.server_url}
        deploymentId={session.deployment_id}
        authToken={session.token}
        encryption={session.encryption_mode}
        theme="light"
        brandName="Messages"
        logoUrl="/icon.png"
        maxAttachmentBytes={MAX_ATTACHMENT_BYTES}
        reactions={REACTIONS}
        emojiBaseUrl="/openmoji/"
        onError={() => setError('Could not connect to messages.')}
      />
    </div>
  )
}
